// Turns API / runtime errors into short, user-safe messages. Server text is only
// surfaced for client errors (4xx) and only when it does not look like an
// internal diagnostic (SQL, stack traces, driver errors).

export const GENERIC_ERROR = 'Something went wrong. Please try again.';

const STATUS_MESSAGES = {
  0: 'Unable to reach the server. Please check your internet connection.',
  400: 'Some of the information provided is invalid. Please review it and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested record could not be found.',
  408: 'The request timed out. Please try again.',
  409: 'This record conflicts with an existing one.',
  413: 'The uploaded file is too large.',
  422: 'Some of the information provided is invalid. Please review it and try again.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'The server encountered a problem. Please try again shortly.',
  502: 'The server is temporarily unavailable. Please try again shortly.',
  503: 'The service is temporarily unavailable. Please try again shortly.',
  504: 'The server took too long to respond. Please try again.',
};

const INTERNAL_PATTERNS = [
  /sql/i, /sequelize/i, /syntax error/i, /\bselect\b.+\bfrom\b/i, /\binsert into\b/i,
  /ER_[A-Z_]+/, /ECONN[A-Z]+/, /ETIMEDOUT/, /stack/i, /\bat [\w.<>]+ \(/, /exception/i,
  /undefined is not/i, /cannot read propert/i, /\bnull\b.*\bconstraint\b/i,
];

export const isSafeServerMessage = (message) =>
  typeof message === 'string' &&
  message.trim().length > 0 &&
  message.length <= 200 &&
  !INTERNAL_PATTERNS.some((pattern) => pattern.test(message));

export const isTimeoutError = (error) =>
  error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT' || /timeout/i.test(error?.message || '');

/**
 * Best user-facing message for an error thrown by the API layer (normalized or raw axios).
 * @param {unknown} error
 * @param {string} [fallback]
 */
export const getErrorMessage = (error, fallback = GENERIC_ERROR) => {
  if (!error) return fallback;
  if (typeof error === 'string') return isSafeServerMessage(error) ? error : fallback;

  const status = error.status ?? error.response?.status;
  const serverMessage = error.data?.message ?? error.response?.data?.message;

  if (status === undefined && error.isAxiosError === undefined) {
    // Non-HTTP error (e.g. a thrown Error in page code) — never expose internals.
    return fallback;
  }
  if (!status) return isTimeoutError(error) ? STATUS_MESSAGES[408] : STATUS_MESSAGES[0];
  if (status >= 400 && status < 500 && isSafeServerMessage(serverMessage)) return serverMessage;
  return STATUS_MESSAGES[status] || (status >= 500 ? STATUS_MESSAGES[500] : fallback);
};
